import { useContext, useEffect, useMemo, useState } from 'react';
import OrgContext from '../../Authentication/components/OrgContext';
import useActivation from '../../Activations/components/hooks/useActivation';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import { EventNavItem, EventQuickLink } from './EventShell';
import {
    IconCapture,
    IconGallery,
    IconInsights,
    IconMedia,
    IconSearch,
    IconSharing,
} from './icons';
import smileMark from '../../smile_icon.png';

/*
 * The event shell's shared data: identity, live posture (rule 19),
 * gallery slug (for quick links), nav and quick-link definitions.
 * Every event-scope page uses this so the rail can't drift between
 * pages. Per-mount fetching is accepted debt, same as useFeatureFlags.
 */

export const LIVE_WINDOW_MS = 45 * 60 * 1000; /* rule 19 */

export default function useEventChrome(id: number) {
    const { org } = useContext(OrgContext);
    const { getActivation } = useActivation();
    const authenticatedApi = useAuthenticatedApi();
    const [activation, setActivation] = useState<any>(null);
    const [photoStats, setPhotoStats] = useState<any>(null);

    /* re-read the header after a page saves the event's name, date or venue */
    useEffect(() => {
        const on = (e: Event) => {
            if (Number((e as CustomEvent).detail) !== id) return;
            getActivation(id)
                .then((a) => setActivation(a))
                .catch(() => {});
        };
        window.addEventListener('ps-event-changed', on);
        return () => window.removeEventListener('ps-event-changed', on);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!id) return;
        let alive = true;
        getActivation(id)
            .then((a) => {
                if (alive) setActivation(a);
            })
            .catch(() => {});
        authenticatedApi
            .get(`/activations/${id}/photoStats`)
            .then((r: any) => {
                if (alive) setPhotoStats(r.data);
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const lastCaptureAt: string | undefined =
        photoStats?.lastPhoto?.createdAt;
    const live =
        !!lastCaptureAt &&
        Date.now() - new Date(lastCaptureAt).getTime() < LIVE_WINDOW_MS;

    const dateLabel = activation?.date
        ? new Date(`${activation.date}T12:00:00`).toLocaleDateString(
              undefined,
              { weekday: 'short', month: 'short', day: 'numeric' },
          )
        : '';
    const eventSub = [dateLabel, activation?.location]
        .filter(Boolean)
        .join(' · ');

    const slug: string | undefined = photoStats?.slug;
    const galleryId: number | undefined = photoStats?.galleryId;

    const quickLinks: EventQuickLink[] = useMemo(() => {
        const links: EventQuickLink[] = [];
        if (slug) {
            links.push(
                {
                    id: 'gallery',
                    label: 'Public gallery',
                    icon: IconGallery,
                    href: `/gallery/${slug}`,
                    copy: true,
                },
                {
                    id: 'station',
                    label: 'Sharing station',
                    icon: IconSharing,
                    href: `/gallery/${slug}/sharing`,
                    copy: true,
                },
                {
                    id: 'slideshow',
                    label: 'Slideshow',
                    icon: IconMedia,
                    href: `/gallery/${slug}/slideshow`,
                    copy: true,
                },
            );
        }
        links.push(
            {
                id: 'analyze',
                label: 'Analyze lookup',
                icon: IconSearch,
                href: `/activations/${id}/analyze`,
            },
            /* these two open the surfaces that manage the magic links */
            {
                id: 'client',
                label: 'Client link',
                icon: IconInsights,
                href: `/activations/${id}/insights`,
            },
            {
                id: 'moderation',
                label: 'Moderation',
                icon: IconCapture,
                href: `/activations/${id}/gallery`,
            },
        );
        return links;
    }, [slug, id]);

    /* areas: native where a v3 surface exists, bridged to the live v2
       event pages otherwise (Integrations waits for a ground) */
    const nav: EventNavItem[][] = useMemo(
        () => [
            [
                { id: 'overview', label: 'Overview', to: `/activations/${id}` },
                { id: 'production', label: 'Production', to: `/activations/${id}/production` },
            ],
            [
                { id: 'gallery', label: 'Gallery', to: `/activations/${id}/gallery` },
                { id: 'capture', label: 'Capture Experience', to: `/activations/${id}/capture` },
                { id: 'media', label: 'Media', to: `/activations/${id}/media` },
                { id: 'sharing', label: 'Sharing & Delivery', to: `/activations/${id}/sharing` },
            ],
            [
                { id: 'insights', label: 'Insights', to: `/activations/${id}/insights` },
                { id: 'payments', label: 'Payments', to: `/activations/${id}/payments` },
            ],
        ],
        [id],
    );

    return {
        org,
        activation,
        photoStats,
        slug,
        galleryId,
        live,
        lastCaptureAt,
        eventSub,
        quickLinks,
        nav,
        /* same initials as the app rail's org mark */
        orgInitials: ((org?.name ?? '').trim().split(/\s+/).filter(Boolean)
            .slice(0, 2).map((w: string) => w[0]).join('') || '·').toUpperCase(),
        /* the org's own logo, else the app's mark (as classic does) */
        orgLogo: org ? ((org.logo as string) || smileMark) : undefined,
        eventName: activation?.name ?? '…',
    };
}
